import {
  useCallback, useState, useEffect, useMemo, ChangeEvent,
} from 'react';
import { toast } from 'react-toastify';
import CompanyService from '../../../services/CompanyService';
import { useAppContext } from '../../../contexts/auth';
import useApiCall from '../../../hooks/useApiCall';
import { BranchType } from './types';

type GetCompanyDataResponseType = {
  codigo: number,
  msg: string,
  validado: boolean,
  codigoRet: number,
  dadosEmpresa: {
    codEmpresa: number,
    tipoEmp: string,
    cnpj: string,
    codMatriz: number,
    inscrEst: string,
    inscrMun: string,
    razaoSoc: string,
    nomeFantasia: string,
    cnpjPagto?: string,
    pagadorNFSeparada?: boolean,
    cnpjCorretoOriginal?: string,
    telDDD: number,
    telNumero: string,
    qtdFunc: number,
    ContratoAtivo: number,
    codUsu: number,
    perfilUsu: number,
    contatoNome: string,
    contatoCPF: string,
    contatoEmail: string,
    telContatoDDD: number,
    telContato: string,
    endFaturamento: string,
    endCodigo: number,
    endTipo: number,
    endCep: string,
    endLogradouro: string,
    endNumero: number,
    endComplemento: string,
    endBairro: string,
    endCidade: string,
    endUF: string,
    endFCodigo: number,
    endFTipo: number,
    endFCep: string,
    endFLogradouro: string,
    endFNumero: number,
    endFComplemento: string,
    endFBairro: string,
    endFCidade: string,
    endFUF: string
  },
  listaEmpresas: {
      codEmpresa: 0,
      tipoEmp: string,
      cnpj: string,
      codMatriz: 0,
      nomeFantasia: string,
      qtdFunc: 0,
      ContratoAtivo: 0
  }[]
}

export default function useBranches() {
  const [isLoading, setIsLoading] = useState(false);
  const [doesApiHasError, setDoesApiHasError] = useState(false);
  const [branches, setBranches] = useState<BranchType[]>([]);

  const [searchTerm, setSearchTerm] = useState('');

  const { selectedCompany, userData } = useAppContext();
  const { userProfile, userCode } = userData;
  const companyCode = selectedCompany.value;

  const { apiCall } = useApiCall();

  const filteredBranches = useMemo(() => branches?.filter((branch) => (
    branch.fantasyName.toLowerCase().includes(searchTerm.toLowerCase())
    || branch.cnpj.toLowerCase().includes(searchTerm.toLowerCase())
  )), [branches, searchTerm]);

  const getCompanyData = useCallback(async () => {
    await apiCall({
      apiToCall: CompanyService.getCompanyData,
      onStartLoad: () => {
        setIsLoading(true);
        setDoesApiHasError(false);
      },
      onEndLoad: () => setIsLoading(false),
      queryParams: {
        codEmpresa: selectedCompany.value,
        codUsu: userCode,
        perfilUsu: userProfile,
      },
      actionAfterResponse: (response: GetCompanyDataResponseType) => {
        if (!response.codigo) {
          toast.error(`Não foi possível carregar os dados das filiais. Por favor, tente novamente (${response.msg})`);
          setDoesApiHasError(true);
          return;
        }
        if (!response.listaEmpresas) {
          setBranches([]);
          return;
        }
        const mappedBranches = response.listaEmpresas.map((branch) => ({
          kind: branch.tipoEmp,
          cnpj: branch.cnpj,
          fantasyName: branch.nomeFantasia,
          employeesQty: branch.qtdFunc,
          active: !!(branch.ContratoAtivo),
        }));
        setBranches(mappedBranches);
      },
      catchMessage: 'Não foi possível carregar os dados das filiais. Por favor, tente novamente.',
      catchAction: () => setDoesApiHasError(true),
    });
  }, [apiCall, selectedCompany.value, userCode, userProfile]);

  function handleChangeSearchTerm(event: ChangeEvent<HTMLInputElement>) {
    setSearchTerm(event.target.value);
  }

  function handleTryAgain() {
    getCompanyData();
  }

  useEffect(() => {
    getCompanyData();
  }, [getCompanyData]);

  return {
    userProfile,
    isLoading,
    companyCode,
    branches,
    apiFetched: !doesApiHasError,
    searchTerm,
    handleChangeSearchTerm,
    filteredBranches,
    handleTryAgain,
  };
}
